<script setup lang="ts">
import { computed, onBeforeMount, ref } from "vue";
import { Popup } from "vant";
import { StoryBook } from "@/types/entity";
import { useStory } from "@/stores/story";
import StoryListItem from "@/components/mobile/story/story-chapter/StoryListItem.vue";
import { storeToRefs } from "pinia";
import CheckCircleFillIcon from "@/components/icon/CheckCircleFillIcon.vue";
// Props & Emit
const props = withDefaults(
  defineProps<{
    show: boolean;
    storyBook?: StoryBook;
    selectedStory: Story | null;
  }>(),
  {
    show: false,
    selectedStory: null,
  }
);

const emit = defineEmits<{
  (e: "update:show", show: boolean): void;
  (e: "update:selectedStory", story: Story | null): void;
  (e: "onConfirm", story: Story): any;
}>();

// State
const storyStore = useStory();
const { storyList } = storeToRefs(storyStore);
const validStoryList = computed<Story[]>(() => {
  // 获取非生成中的故事
  if (props.storyBook) {
    if (!storyStore.storyBookId2StoryUuids[props.storyBook.id]) return [];
    return storyStore.storyBookId2StoryUuids[props.storyBook.id].map(
      (uuid) => storyStore.storyMap[uuid]
    );
  } else {
    return storyList.value.filter((item) => item.description);
  }
});
const loading = ref(false);

// Lifecycle
const prepareData = async () => {
  loading.value = true;
  try {
    if (props.storyBook) {
      await storyStore.getStoryListByStoryBook(props.storyBook);
    } else {
      await storyStore.getStoryList();
    }
  } finally {
    loading.value = false;
  }
};
onBeforeMount(prepareData);

// EventHandler
const onSelect = (story: Story) => {
  emit("update:selectedStory", story);
};

const reset = async () => {
  emit("update:selectedStory", null);
};

export interface MomentSelectionInstance {
  reset: () => Promise<void>;
}

defineExpose<MomentSelectionInstance>({
  reset,
});
</script>
<template>
  <popup
    position="bottom"
    class="max-h-[80%] overflow-hidden grid grid-rows-[auto_1fr]"
    :show="show"
    @update:show="emit('update:show', $event)"
    round
    teleport="body"
  >
    <div class="navbar h-top-nav border-b">
      <!-- 左 -->
      <div class="navbar-start h-full">
        <button class="btn btn-sm rounded" @click="emit('update:show', false)">
          {{ $t("Buttons.cancel") }}
        </button>
      </div>
      <!-- 中 -->
      <div class="navbar-center">
        <div class="font-bold">{{ $t("story.selectChapter") }}</div>
      </div>
      <!-- 右 -->
      <div class="navbar-end">
        <button
          class="btn btn-primary btn-sm rounded"
          :disabled="selectedStory === null"
          @click="
            emit('onConfirm', selectedStory!);
            emit('update:show', false);
          "
        >
          {{ $t("Buttons.confirm") }}
        </button>
      </div>
    </div>
    <div class="w-full overflow-hidden flex flex-col relative">
      <div class="overflow-y-auto w-full h-full" ref="scrollableEl">
        <div v-show="loading" class="h-40 flex items-center justify-center">
          <span class="loading"></span>
        </div>
        <div
          v-show="!loading && validStoryList.length === 0"
          class="h-40 flex flex-col items-center justify-center"
        >
          <div class="text-2xl font-bold text-base-content/50">
            {{ $t("story.noChaptersAvailable") }}
          </div>
          <div class="text-base-content/50">
            {{ $t("story.currentCreatedChapters") }}
          </div>
        </div>
        <div v-show="!loading && validStoryList.length > 0" class="p-2 pt-0">
          <div
            v-for="item in validStoryList"
            :key="item.id"
            class="relative"
            :class="{
              'border-primary border rounded': selectedStory?.id === item.id,
              'border border-transparent ': selectedStory?.id !== item.id,
            }"
            @click="onSelect(item)"
          >
            <story-list-item class="px-2" :story="item" />
            <check-circle-fill-icon
              v-show="selectedStory?.id === item.id"
              class="text-primary absolute right-1 bottom-1 icon-xl rounded-full"
            />
          </div>
        </div>
      </div>
    </div>
  </popup>
</template>
